import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import Supercluster from "supercluster";
import { TEAM_MEMBERS, CITY_LATLNG } from "../data/teamMembers";

// Bypass the preview iframe's fetch-monkey-patch by registering a custom
// Image-based protocol for raster tiles.
let _protoRegistered = false;
function ensureXhrProtocol() {
  if (_protoRegistered) return;
  _protoRegistered = true;
  maplibregl.addProtocol("xhr", (params) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve({ data: img });
      img.onerror = () => reject(new Error("Tile error: " + params.url));
      img.src = params.url.replace(/^xhr:\/\//, "https://");
    });
  });
}

// Resolve members to {lng, lat} with optional offsets
const resolvedMembers = TEAM_MEMBERS.map((m) => {
  const base = CITY_LATLNG[m.cityKey] || { lng: -95, lat: 60 };
  const dx = m.offset?.lngOffset ?? 0;
  const dy = m.offset?.latOffset ?? 0;
  return { ...m, lng: base.lng + dx, lat: base.lat + dy };
});

// Group members that share the exact same location into a single "team" entry.
// A grouped entry renders as a single bull-head pin; hovering it lists every
// teammate at that location in the popup.
const groupedEntries = (() => {
  const groups = new Map();
  resolvedMembers.forEach((m) => {
    const key = `${m.lng.toFixed(4)},${m.lat.toFixed(4)}`;
    if (!groups.has(key)) {
      groups.set(key, { lng: m.lng, lat: m.lat, members: [] });
    }
    groups.get(key).members.push(m);
  });
  let i = 0;
  return Array.from(groups.values()).map((g) => {
    i += 1;
    const single = g.members.length === 1 ? g.members[0] : null;
    return {
      id: single ? single.id : `grp-${i}`,
      lng: g.lng,
      lat: g.lat,
      members: g.members,
    };
  });
})();

const MAP_STYLE = {
  version: 8,
  sources: {
    "esri-base": {
      type: "raster",
      tiles: [
        "xhr://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution:
        'Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin',
    },
    "esri-dark-ref": {
      type: "raster",
      tiles: [
        "xhr://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
    },
  },
  layers: [
    // Dark base so the canvas starts dark; the CSS filter flips tile
    // luminance (invert + hue-rotate) to produce dark land + light water
    // with the flat / crisp "dark-matter" aesthetic.
    { id: "bg", type: "background", paint: { "background-color": "#02050f" } },
    {
      id: "esri-base",
      type: "raster",
      source: "esri-base",
      paint: {
        "raster-saturation": 0,
      },
    },
    {
      id: "esri-dark-ref",
      type: "raster",
      source: "esri-dark-ref",
      paint: {
        "raster-opacity": 0.55,
      },
    },
  ],
};

function computeBounds() {
  const lngs = resolvedMembers.map((m) => m.lng);
  const lats = resolvedMembers.map((m) => m.lat);
  return [
    [Math.min(...lngs) - 4, Math.min(...lats) - 2],
    [Math.max(...lngs) + 4, Math.max(...lats) + 4],
  ];
}

// Build a supercluster index once
const buildSupercluster = () => {
  const sc = new Supercluster({
    radius: 28, // smaller cluster radius — pins separate at lower zoom
    maxZoom: 5, // beyond zoom 5 every pin is shown individually
    minPoints: 2,
  });
  sc.load(
    groupedEntries.map((g) => ({
      type: "Feature",
      properties: { entryId: g.id },
      geometry: { type: "Point", coordinates: [g.lng, g.lat] },
    }))
  );
  return sc;
};

function createMemberEl(member) {
  const el = document.createElement("div");
  el.className = "ctm-marker";
  el.setAttribute("data-testid", `map-node-${member.id}`);
  el.innerHTML =
    '<span class="ctm-marker-pulse"></span>' +
    '<span class="ctm-marker-halo"></span>' +
    '<img class="ctm-marker-logo" src="/bull-head.png" alt="" draggable="false" />';
  return el;
}

function createClusterEl(count, ids) {
  const el = document.createElement("div");
  el.className = "ctm-cluster";
  el.setAttribute("data-testid", `map-cluster-${count}`);
  el.setAttribute("data-cluster-ids", ids.join(","));
  el.innerHTML =
    '<span class="ctm-cluster-pulse"></span>' +
    '<span class="ctm-cluster-halo"></span>' +
    `<span class="ctm-cluster-bubble"><span class="ctm-cluster-count">${count}</span></span>`;
  return el;
}

export default function CanadaTeamMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]); // current maplibregl.Marker instances
  const superclusterRef = useRef(null);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    ensureXhrProtocol();
    superclusterRef.current = buildSupercluster();

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-95, 64],
      zoom: 1.6,
      bearing: 0,
      pitch: 0,
      attributionControl: { compact: true },
      maxPitch: 60,
      dragRotate: false,
      pitchWithRotate: false,
    });
    mapRef.current = map;

    // Update markers + clusters when the map moves
    const updateMarkers = () => {
      const sc = superclusterRef.current;
      if (!sc) return;
      const bounds = map.getBounds();
      const bbox = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];
      const zoom = Math.floor(map.getZoom());
      const clusters = sc.getClusters(bbox, zoom);

      // Remove existing markers
      markersRef.current.forEach((mk) => mk.remove());
      markersRef.current = [];

      clusters.forEach((c) => {
        const [lng, lat] = c.geometry.coordinates;
        if (c.properties.cluster) {
          // CLUSTER pin
          const leaves = sc.getLeaves(c.properties.cluster_id, Infinity);
          const entries = leaves.map((l) =>
            groupedEntries.find((g) => g.id === l.properties.entryId)
          );
          const members = entries.flatMap((g) => g?.members || []);
          const el = createClusterEl(members.length, members.map((m) => m.id));

          el.addEventListener("mouseenter", (evt) => {
            const rect = containerRef.current.getBoundingClientRect();
            setPopup({
              type: "cluster",
              members,
              x: evt.clientX - rect.left,
              y: evt.clientY - rect.top,
            });
            el.classList.add("is-active");
          });
          el.addEventListener("mousemove", (evt) => {
            const rect = containerRef.current.getBoundingClientRect();
            setPopup((p) =>
              p
                ? { ...p, x: evt.clientX - rect.left, y: evt.clientY - rect.top }
                : p
            );
          });
          el.addEventListener("mouseleave", () => {
            setPopup(null);
            el.classList.remove("is-active");
          });
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            const expansionZoom = Math.min(
              sc.getClusterExpansionZoom(c.properties.cluster_id),
              16
            );
            map.flyTo({ center: [lng, lat], zoom: expansionZoom, duration: 900 });
          });

          const mk = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([lng, lat])
            .addTo(map);
          markersRef.current.push(mk);
        } else {
          // Individual entry (one or more members at the same exact location)
          const entry = groupedEntries.find(
            (g) => g.id === c.properties.entryId
          );
          if (!entry) return;
          const isTeam = entry.members.length > 1;
          const primary = entry.members[0];
          const el = createMemberEl(primary);

          el.addEventListener("mouseenter", (evt) => {
            const rect = containerRef.current.getBoundingClientRect();
            setPopup(
              isTeam
                ? {
                    type: "cluster",
                    members: entry.members,
                    x: evt.clientX - rect.left,
                    y: evt.clientY - rect.top,
                  }
                : {
                    type: "member",
                    member: primary,
                    x: evt.clientX - rect.left,
                    y: evt.clientY - rect.top,
                  }
            );
            el.classList.add("is-active");
          });
          el.addEventListener("mousemove", (evt) => {
            const rect = containerRef.current.getBoundingClientRect();
            setPopup((p) =>
              p
                ? { ...p, x: evt.clientX - rect.left, y: evt.clientY - rect.top }
                : p
            );
          });
          el.addEventListener("mouseleave", () => {
            setPopup(null);
            el.classList.remove("is-active");
          });
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            map.flyTo({
              center: [entry.lng, entry.lat],
              zoom: 6.5,
              duration: 1200,
            });
          });

          const mk = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([entry.lng, entry.lat])
            .addTo(map);
          markersRef.current.push(mk);
        }
      });
    };

    map.on("load", () => {
      updateMarkers();

      const bounds = computeBounds();
      setTimeout(() => {
        map.fitBounds(bounds, {
          padding: { top: 80, bottom: 160, left: 80, right: 80 },
          duration: 2200,
          pitch: 28,
          bearing: 0,
          essential: true,
        });
      }, 250);
    });

    map.on("moveend", updateMarkers);
    map.on("zoomend", updateMarkers);

    return () => {
      markersRef.current.forEach((mk) => mk.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ---------- UI controls ----------
  const zoomIn = useCallback(
    () => mapRef.current?.zoomIn({ duration: 300 }),
    []
  );
  const zoomOut = useCallback(
    () => mapRef.current?.zoomOut({ duration: 300 }),
    []
  );
  const fitAll = useCallback(() => {
    mapRef.current?.fitBounds(computeBounds(), {
      padding: { top: 80, bottom: 160, left: 80, right: 80 },
      duration: 1100,
      pitch: 28,
    });
  }, []);

  return (
    <div className="ctm-root" data-testid="canada-team-map-root">
      <div ref={containerRef} className="ctm-map" />

      <div className="ctm-vignette" aria-hidden="true" />
      <div className="ctm-fade" aria-hidden="true" />

      <div className="ctm-controls" data-testid="map-controls">
        <button
          className="ctm-btn"
          onClick={zoomIn}
          aria-label="Zoom in"
          data-testid="btn-zoom-in"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          className="ctm-btn"
          onClick={zoomOut}
          aria-label="Zoom out"
          data-testid="btn-zoom-out"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          className="ctm-btn ctm-btn-fit"
          onClick={fitAll}
          aria-label="Fit all team members"
          data-testid="btn-fit-all"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="ctm-hint" aria-hidden="true">
        scroll to zoom · drag to pan · click clusters to expand
      </div>

      {/* Tooltip / popup */}
      {popup && popup.type === "member" && (
        <div
          className="ctm-tooltip"
          style={{ left: popup.x, top: popup.y }}
          data-testid={`map-tooltip-${popup.member.id}`}
          role="tooltip"
        >
          <div className="ctm-tooltip-inner">
            <div
              className="ctm-tooltip-line ctm-tooltip-name"
              data-testid="tooltip-name"
            >
              {popup.member.name}
            </div>
            <div
              className="ctm-tooltip-line ctm-tooltip-city"
              data-testid="tooltip-city"
            >
              {popup.member.city}
            </div>
            <div
              className="ctm-tooltip-line ctm-tooltip-territory"
              data-testid="tooltip-territory"
            >
              {popup.member.territory}
            </div>
          </div>
        </div>
      )}

      {popup && popup.type === "cluster" && (
        <div
          className="ctm-tooltip ctm-tooltip-cluster"
          style={{ left: popup.x, top: popup.y }}
          data-testid="map-tooltip-cluster"
          role="tooltip"
        >
          <div className="ctm-tooltip-inner">
            <div className="ctm-tooltip-cluster-heading">
              {popup.members.length} team members here
            </div>
            <ul className="ctm-tooltip-cluster-list">
              {popup.members.map((m) => (
                <li key={m.id}>
                  <span className="ctm-tooltip-cluster-name">{m.name}</span>
                  <span className="ctm-tooltip-cluster-meta">
                    {m.territory}
                  </span>
                </li>
              ))}
            </ul>
            <div className="ctm-tooltip-cluster-hint">click to zoom in</div>
          </div>
        </div>
      )}
    </div>
  );
}
